import { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';

// Auth
import { useAuthStore } from './stores/authStore';
import { trackPixel } from './lib/metaPixel';
import { ProtectedRoute } from './components/ProtectedRoute';
import { AuthCallback } from './pages/auth/AuthCallback';

// Auth Pages
import { Login } from './pages/auth/Login';
import { Signup } from './pages/auth/Signup';
import { ForgotPassword } from './pages/auth/ForgotPassword';
import { ResetPassword } from './pages/auth/ResetPassword';
import { JoinWorkspace } from './pages/auth/JoinWorkspace';
import { Verification } from './pages/auth/Verification';

// App Pages
import { Overview } from './pages/app/Overview';
import { Prompts } from './pages/app/Prompts';
import { Competitors } from './pages/app/Competitors';
import { Mentions } from './pages/app/Mentions';
import { Searches } from './pages/app/Searches';
import { Insights } from './pages/app/Insights';
import { AIAnalysis } from './pages/app/AIAnalysis';
import { Plans } from './pages/app/Plans';

// Disabled pages (kept for potential future use):
// import { Designs } from './pages/app/Designs';
// import { Templates } from './pages/app/Templates';
// import { Canvas } from './pages/app/Canvas';

// Onboarding
import { OnboardingStep1 } from './pages/onboarding/Step1';
import { OnboardingStep2 } from './pages/onboarding/Step2';
import { OnboardingStep3 } from './pages/onboarding/Step3';
import { OnboardingStep4 } from './pages/onboarding/Step4';
import { OnboardingPayment } from './pages/onboarding/Payment';

// Layout
import { AppLayout } from './components/AppLayout';
import { AuthLayout } from './components/AuthLayout';
import { OnboardingRoute } from './components/OnboardingRoute';
import { StaffRoute } from './components/StaffRoute';

// Staff
import { StaffSearch } from './pages/staff/Search';
import { StaffUser } from './pages/staff/UserDetail';
import { StaffWorkspace } from './pages/staff/WorkspaceDetail';
import { StaffActivity } from './pages/staff/Activity';

// UI
import { ToastContainer } from './components/Toast';
import { ViewAsBanner } from './components/ViewAsBanner';

// Styles
import './styles/index.css';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5, // 5 minutes
      retry: 1,
    },
  },
});

function AppRoutes() {
  const { loading, initialized, initialize } = useAuthStore();
  const { pathname } = useLocation();

  useEffect(() => {
    initialize();
  }, [initialize]);

  useEffect(() => {
    if (!useAuthStore.getState().viewingAs) trackPixel('PageView');
  }, [pathname]);

  if (!initialized || loading) {
    return (
      <div className="loading-screen">
        <div className="loading-spinner" />
      </div>
    );
  }

  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/" element={<Navigate to="/login" replace />} />
      
      {/* Auth Routes */}
      <Route element={<AuthLayout />}>
        <Route path="/login" element={<Login />} />
        <Route path="/signup" element={<Signup />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
      </Route>
      
      {/* Standalone auth pages (no nav) */}
      <Route path="/verification" element={<Verification />} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/join-workspace" element={<JoinWorkspace />} />

      {/* Protected App Routes */}
      <Route element={<ProtectedRoute />}>
        {/* Onboarding - Only accessible if not completed */}
        <Route element={<OnboardingRoute />}>
          <Route path="/onboarding/step-1" element={<OnboardingStep1 />} />
          <Route path="/onboarding/step-2" element={<OnboardingStep2 />} />
          <Route path="/onboarding/step-3" element={<OnboardingStep3 />} />
          <Route path="/onboarding/step-4" element={<OnboardingStep4 />} />
          <Route path="/onboarding/payment" element={<OnboardingPayment />} />
        </Route>

        {/* Main App with Sidebar */}
        <Route element={<AppLayout />}>
          <Route path="/overview" element={<Overview />} />
          <Route path="/analytics" element={<Navigate to="/overview" replace />} />
          <Route path="/prompts" element={<Prompts />} />
          <Route path="/competitors" element={<Competitors />} />
          <Route path="/mentions" element={<Mentions />} />
          <Route path="/searches" element={<Searches />} />
          <Route path="/insights" element={<Insights />} />
          <Route path="/audit" element={<AIAnalysis />} />
          <Route path="/ai-audit" element={<Navigate to="/audit" replace />} />
          <Route path="/ai-analysis" element={<Navigate to="/audit" replace />} />
          <Route path="/plans" element={<Plans />} />
        </Route>

        {/* Staff panel - Staff only, MFA verified */}
        <Route path="/staff" element={<StaffRoute />}>
          <Route index element={<StaffSearch />} />
          <Route path="users/:id" element={<StaffUser />} />
          <Route path="workspaces/:id" element={<StaffWorkspace />} />
          <Route path="activity" element={<StaffActivity />} />
          <Route path="*" element={<Navigate to="/staff" replace />} />
        </Route>
      </Route>

      {/* 404 */}
      <Route path="*" element={<Navigate to="/login" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <ViewAsBanner />
        <AppRoutes />
        <ToastContainer />
      </BrowserRouter>
    </QueryClientProvider>
  );
}
